import api from './client';
import type { App, AppCampaign, CreateAppInput, UpdateAppInput } from './types';

/**
 * Get all apps
 */
export async function getApps(): Promise<App[]> {
  return api.get<App[]>('/admin/apps');
}

export interface AppOwnerOption {
  id: string;
  email: string;
  name: string | null;
}

export async function getAppOwners(): Promise<AppOwnerOption[]> {
  return api.get<AppOwnerOption[]>('/admin/apps/owners');
}

/**
 * Get a single app by ID
 */
export async function getApp(id: string): Promise<App> {
  return api.get<App>(`/admin/apps/${id}`);
}

/**
 * Create a new app
 */
export async function createApp(data: CreateAppInput): Promise<App> {
  return api.post<App>('/admin/apps', data);
}

/**
 * Update an existing app
 */
export async function updateApp(id: string, data: UpdateAppInput): Promise<App> {
  return api.put<App>(`/admin/apps/${id}`, data);
}

/**
 * Delete an app (soft delete)
 */
export async function deleteApp(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/apps/${id}`);
}

/**
 * Get campaigns assigned to an app
 */
export async function getAppCampaigns(appId: string): Promise<AppCampaign[]> {
  return api.get<AppCampaign[]>(`/admin/apps/${appId}/campaigns`);
}

/**
 * Assign campaigns to an app
 */
export async function assignCampaigns(appId: string, campaignIds: string[]): Promise<any> {
  return api.post(`/admin/apps/${appId}/campaigns`, { campaign_ids: campaignIds });
}

/**
 * Remove a campaign assignment from an app
 */
export async function unassignCampaign(appId: string, campaignId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/apps/${appId}/campaigns/${campaignId}`);
}

/**
 * Get geo coverage for an app: which geos active ads target, and which are free.
 */
export interface AppGeos {
  running: string[];
  offered: string[];
  free: string[];
  ads_total: number;
  ads_untargeted: number;
}

export async function getAppGeos(appId: string): Promise<AppGeos> {
  return api.get<AppGeos>(`/admin/apps/${appId}/geos`);
}

/**
 * Manually refresh store versions for all active apps
 */
export async function refreshStoreVersions(): Promise<{ message: string; updated: number }> {
  return api.post<{ message: string; updated: number }>('/admin/apps/refresh-store-versions', {});
}

/** On-demand poll of one app's Play production review state. Returns the fresh
 *  state; the badge in the Apps table reflects it after the query refetches. */
export async function refreshAppReview(
  id: string
): Promise<{ review_state: string | null; review_state_since: string | null; review_version: string | null }> {
  return api.post(`/admin/apps/${id}/refresh-review`, {});
}

/** "Turn on now": the force offer stops waiting out its hold after a release / update / spike. */
export async function clearOfferHold(id: string): Promise<{ cleared: boolean }> {
  return api.post(`/admin/apps/${id}/offer-hold/clear`, {});
}

export interface GoogleAdsPublishResult {
  success: boolean;
  ok: boolean;
  applied?: boolean;
  app?: string | null;
  bundle?: string | null;
  created?: string[];
  errors?: string[];
}

/** Hand one app to the external Google Ads Publish API, which creates an App
 *  campaign for it and stamps advertised_at. `validate_only` only checks on
 *  Google's side. Fails with 503 until the token is set in Settings. */
export async function publishToGoogleAds(
  id: string,
  opts: { validate_only?: boolean } = {},
): Promise<GoogleAdsPublishResult> {
  return api.post(`/admin/apps/${id}/google-ads-publish`, opts);
}

// --- Comments (edit history kept on the server) ---
export interface AppCommentVersion {
  body: string;
  edited_at: number;
  edited_by_name: string | null;
}
export interface AppComment {
  id: number;
  app_id: string;
  body: string;
  author_id: string | null;
  author_name: string | null;
  created_at: number;
  updated_at: number | null;
  updated_by_name: string | null;
  deleted_at: number | null;
  deleted_by_name: string | null;
  can_edit: boolean;
  /** Earlier texts, oldest first: each is the body as it was before an edit. */
  versions: AppCommentVersion[];
}

export function getAppComments(appId: string): Promise<AppComment[]> {
  return api.get<AppComment[]>(`/admin/apps/${appId}/comments`);
}
export function addAppComment(appId: string, body: string): Promise<AppComment> {
  return api.post<AppComment>(`/admin/apps/${appId}/comments`, { body });
}
export function editAppComment(appId: string, commentId: number, body: string): Promise<{ id: number }> {
  return api.put<{ id: number }>(`/admin/apps/${appId}/comments/${commentId}`, { body });
}
export function deleteAppComment(appId: string, commentId: number): Promise<{ id: number }> {
  return api.delete<{ id: number }>(`/admin/apps/${appId}/comments/${commentId}`);
}

// --- Countries the app is open in on Google Play ---
export function refreshPlayCountries(appId: string): Promise<{ countries: string[]; rest_of_world: boolean }> {
  return api.post(`/admin/apps/${appId}/play-countries/refresh`);
}

// --- Firebase straight from an Apps row (no Apps Setup import needed) ---
export function registerAppFirebase(
  appId: string, input: { firebase_account_id?: number; firebase_org_id?: number },
): Promise<{ firebase_project_id?: string; firebase_android_app_id?: string; firebase_provision_id?: number }> {
  return api.post(`/admin/apps/${appId}/firebase`, input);
}
export async function downloadAppGoogleServices(appId: string): Promise<Blob> {
  return api.blob(`/admin/apps/${appId}/google-services.json`);
}

// Reg/dep to Google Ads through Firebase: the Measurement Protocol secret of
// the app's GA4 stream and how the sends are going.
export interface AppGa4Mp {
  firebase_app_id: string | null;
  registered_firebase_app_id: string | null;
  firebase_app_id_override: string | null;
  has_secret: boolean;
  secret_hint: string | null;
  source: 'auto' | 'manual' | null;
  stream: string | null;
  setup_error: string | null;
  setup_attempted_at: number | null;
  sent_30d: Record<string, number>;
  last_send_error: string | null;
  last_send_error_at: string | null;
}
export function getAppGa4Mp(appId: string): Promise<AppGa4Mp> {
  return api.get(`/admin/apps/${appId}/ga4-mp`);
}
export function setAppGa4Mp(appId: string, input: { api_secret: string; firebase_app_id?: string }): Promise<AppGa4Mp> {
  return api.put(`/admin/apps/${appId}/ga4-mp`, input);
}
export function setupAppGa4Mp(appId: string): Promise<AppGa4Mp> {
  return api.post(`/admin/apps/${appId}/ga4-mp/setup`, {});
}
